"use client"

import React, { createContext, useContext, useState, ReactNode } from "react"

export interface MockPhoto {
    id: string
    url: string
    name: string
    size: number
    uploadedAt: string
    takenAt?: string // Date when photo was taken (from EXIF)
    homeId: string
    workOrderId?: string
    workOrderNumber?: string // Work order number like "EF-00060"
}

export interface PhotoGroup {
    date: string
    displayDate: string
    photos: MockPhoto[]
}

interface PhotosContextType {
    photos: MockPhoto[]
    addPhotos: (photos: MockPhoto[]) => void
    removePhoto: (photoId: string) => void
    getPhotosByHomeId: (homeId: string) => MockPhoto[]
    getPhotosGroupedByDate: (homeId: string) => PhotoGroup[]
}

const PhotosContext = createContext<PhotosContextType | undefined>(undefined)

interface PhotosProviderProps {
    children: ReactNode
}

export function PhotosProvider({ children }: PhotosProviderProps) {
    // Initialize with some mock data to demonstrate timeline functionality
    const [photos, setPhotos] = useState<MockPhoto[]>(() => {
        const now = new Date()
        const today = new Date(now)
        const yesterday = new Date(now)
        yesterday.setDate(yesterday.getDate() - 1)
        const twoDaysAgo = new Date(now)
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2)
        const weekAgo = new Date(now)
        weekAgo.setDate(weekAgo.getDate() - 7)

        return [
            {
                id: "demo-1",
                url: "/images/mock-photo-1.jpg",
                name: "House Exterior - Today",
                size: 1024000,
                uploadedAt: today.toISOString(),
                takenAt: today.toISOString(),
                homeId: "1",
                workOrderId: "WO-001",
                workOrderNumber: "EF-00060",
            },
            {
                id: "demo-2",
                url: "/images/mock-photo-2.jpg",
                name: "Kitchen Damage - Today",
                size: 2048000,
                uploadedAt: today.toISOString(),
                takenAt: today.toISOString(),
                homeId: "1",
                workOrderId: "WO-001",
                workOrderNumber: "EF-00060",
            },
            {
                id: "demo-3",
                url: "/images/mock-photo-3.jpg",
                name: "Living Room - Yesterday",
                size: 1536000,
                uploadedAt: yesterday.toISOString(),
                takenAt: yesterday.toISOString(),
                homeId: "1",
                workOrderId: "WO-002",
                workOrderNumber: "EF-00061",
            },
            {
                id: "demo-4",
                url: "/images/mock-photo-1.jpg",
                name: "Bathroom - Two Days Ago",
                size: 1800000,
                uploadedAt: twoDaysAgo.toISOString(),
                takenAt: twoDaysAgo.toISOString(),
                homeId: "1",
            },
            {
                id: "demo-5",
                url: "/images/mock-photo-2.jpg",
                name: "Bedroom - Week Ago",
                size: 2100000,
                uploadedAt: weekAgo.toISOString(),
                takenAt: weekAgo.toISOString(),
                homeId: "1",
                workOrderId: "WO-003",
                workOrderNumber: "EF-00062",
            },
        ]
    })

    const addPhotos = (newPhotos: MockPhoto[]) => {
        setPhotos((prev) => [...prev, ...newPhotos])
    }

    const removePhoto = (photoId: string) => {
        setPhotos((prev) => prev.filter((photo) => photo.id !== photoId))
    }

    const getPhotosByHomeId = (homeId: string) => {
        return photos.filter((photo) => photo.homeId === homeId)
    }

    const getPhotosGroupedByDate = (homeId: string) => {
        const homePhotos = getPhotosByHomeId(homeId)

        // Group photos by date (using takenAt if available, otherwise uploadedAt)
        const grouped = homePhotos.reduce((groups, photo) => {
            const dateToUse = photo.takenAt || photo.uploadedAt
            const date = new Date(dateToUse).toDateString()

            if (!groups[date]) {
                groups[date] = []
            }
            groups[date].push(photo)
            return groups
        }, {} as Record<string, MockPhoto[]>)

        // Convert to array and sort by date (most recent first)
        return Object.entries(grouped)
            .map(([date, photos]) => ({
                date,
                displayDate: formatDisplayDate(date),
                photos: photos.sort((a, b) => {
                    const dateA = new Date(a.takenAt || a.uploadedAt)
                    const dateB = new Date(b.takenAt || b.uploadedAt)
                    return dateB.getTime() - dateA.getTime() // Most recent first
                }),
            }))
            .sort(
                (a, b) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
            )
    }

    const formatDisplayDate = (dateString: string) => {
        const date = new Date(dateString)
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)

        if (date.toDateString() === today.toDateString()) {
            return "Today"
        } else if (date.toDateString() === yesterday.toDateString()) {
            return "Yesterday"
        } else {
            return date.toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
            })
        }
    }

    return (
        <PhotosContext.Provider
            value={{
                photos,
                addPhotos,
                removePhoto,
                getPhotosByHomeId,
                getPhotosGroupedByDate,
            }}
        >
            {children}
        </PhotosContext.Provider>
    )
}

export function usePhotos() {
    const context = useContext(PhotosContext)
    if (context === undefined) {
        throw new Error("usePhotos must be used within a PhotosProvider")
    }
    return context
}
